import React, { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as z from 'zod';
import { MediaProviders } from 'constants/media';
import {
  CoachSportStatusesEnum,
  GenderEnum,
  GetCoachSettingsQuery,
  SportsEnum,
  useUpdateOnboardingStep1Mutation,
} from 'types/generated/client';
import { toDigits } from 'utils/shared/phone/toDigits';
import { useRequestStatus } from 'hooks/useRequestStatus';
import { useUploadSignedFile } from 'hooks/useUploadSignedFile';
import { useViewer } from 'hooks/useViewer';
import Button from 'components/Button';
import Dropzone from 'components/Dropzone';
import InputField from 'components/coaches-builder/InputField';
import { InputType } from 'components/coaches-builder/InputField/InputField';
import ProfileItem from 'components/coaches-builder/ProfileItem';
import RadioList from 'components/forms/RadioList';
import CheckboxType from 'components/forms/checkboxes/CheckBoxType';

interface ProfileStepProps {
  onSubmit: () => void;
  coachData?: GetCoachSettingsQuery['usersByPk'];
  isLoading?: boolean;
}

const sportsOptions = [
  { label: 'Pickleball', value: SportsEnum.Pickleball },
  { label: 'Tennis', value: SportsEnum.Tennis },
  { label: 'Padel', value: SportsEnum.Padel },
];

const genderOptions = [
  { value: GenderEnum.Male, label: 'Male' },
  { value: GenderEnum.Female, label: 'Female' },
];

const profileStepSchema = z.object({
  profilePhoto: z.any().optional(),
  gender: z.enum([GenderEnum.Male, GenderEnum.Female], {
    required_error: 'Please select your gender',
  }),
  sports: z
    .object({
      [SportsEnum.Pickleball]: z.boolean().optional(),
      [SportsEnum.Tennis]: z.boolean().optional(),
      [SportsEnum.Padel]: z.boolean().optional(),
    })
    .refine(
      (data) => {
        return Object.values(data).some((value) => value === true);
      },
      {
        message: 'Please select at least one sport',
      },
    ),
  phoneNumber: z.string().min(8, 'Phone number is required'),
  phoneCountryCode: z.string().optional(),
});

type ProfileStepForm = z.infer<typeof profileStepSchema>;

const ProfileStep = ({ onSubmit, coachData, isLoading }: ProfileStepProps) => {
  const viewer = useViewer();
  const { uploadSignedFile } = useUploadSignedFile();
  const [updateOnboardingStep1Mutation, { loading: isUpdating }] =
    useUpdateOnboardingStep1Mutation();
  const {
    isLoading: isImageLoading,
    setLoading: setImageLoading,
    setSuccess: setImageSuccess,
    setError: setImageError,
  } = useRequestStatus();

  const {
    control,
    register,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileStepForm>({
    resolver: zodResolver(profileStepSchema),
    defaultValues: {
      profilePhoto: undefined,
      gender: GenderEnum.Male,
      sports: {
        [SportsEnum.Pickleball]: false,
        [SportsEnum.Tennis]: false,
        [SportsEnum.Padel]: false,
      },
      phoneNumber: '',
      phoneCountryCode: '+1',
    },
  });

  // Pre-populate form with existing data
  useEffect(() => {
    if (coachData && !isLoading) {
      // Set gender
      if (
        coachData.gender &&
        (coachData.gender === GenderEnum.Male || coachData.gender === GenderEnum.Female)
      ) {
        setValue('gender', coachData.gender);
      }

      const preSelectedSports = coachData.coachSports.map(
        (coachSport) => coachSport.sport as SportsEnum,
      );

      console.log('preSelectedSports', preSelectedSports);
      if (preSelectedSports.length) {
        // Set each sport individually
        setValue('sports', {
          [SportsEnum.Pickleball]: preSelectedSports.includes(SportsEnum.Pickleball),
          [SportsEnum.Tennis]: preSelectedSports.includes(SportsEnum.Tennis),
          [SportsEnum.Padel]: preSelectedSports.includes(SportsEnum.Padel),
        });
      }
      console.log(
        'coachData.phoneNumber , coachData.phoneCountryCode',
        coachData.phoneNumber,
        coachData.phoneCountryCode,
      );

      if (coachData.phoneNumber) {
        if (coachData.phoneCountryCode) {
          setValue('phoneNumber', `${coachData.phoneCountryCode}${coachData.phoneNumber}`);
          setValue('phoneCountryCode', coachData.phoneCountryCode);
        } else {
          setValue('phoneNumber', coachData.phoneNumber);
        }
      }

      // Set profile photo URL if exists
      if (coachData.profileImageUrl) {
        setValue('profilePhoto', coachData.profileImageUrl);
      }
    }
  }, [coachData, isLoading, setValue]);

  const handleSaveAndContinue = async (data: ProfileStepForm) => {
    try {
      // Handle profile photo upload if it's a file
      let photoData = null;
      if (data.profilePhoto && data.profilePhoto instanceof File) {
        try {
          setImageLoading();
          const response = await uploadSignedFile({ file: data.profilePhoto });

          if (response) {
            photoData = {
              profileImageFileName: response.fileName,
              profileImagePath: response.path,
              profileImageProvider: MediaProviders.Cloudflare,
              profileImageProviderId: response.fileName.split('.')[0],
              profileImageProviderUrl: response.providerUrl,
              profileImageUrl: response.url,
            };
          }
          setImageSuccess();
        } catch (error) {
          setImageError('');
          toast.error('Failed to upload profile photo');
          return;
        }
      }

      // Convert sports object to array for mutation
      const selectedSports = Object.entries(data.sports)
        .filter(([_, isSelected]) => isSelected)
        .map(([sport, _]) => sport as SportsEnum);

      // Prepare sports data in the correct format
      const sportsData = selectedSports.map((sport) => ({
        userId: viewer.userId,
        sport,
        status: CoachSportStatusesEnum.Active,
      }));

      // Determine default sport based on priority: Pickleball > Tennis > Padel
      let defaultSport = SportsEnum.Pickleball; // fallback
      if (selectedSports.includes(SportsEnum.Pickleball)) {
        defaultSport = SportsEnum.Pickleball;
      } else if (selectedSports.includes(SportsEnum.Tennis)) {
        defaultSport = SportsEnum.Tennis;
      } else if (selectedSports.includes(SportsEnum.Padel)) {
        defaultSport = SportsEnum.Padel;
      }

      // Prepare mutation variables
      const mutationVariables = {
        userId: viewer.userId,
        gender: data.gender,
        phoneNumber: toDigits(data.phoneNumber || '')?.slice(data.phoneCountryCode?.length || 0),
        phoneCountryCode: toDigits(data.phoneCountryCode || ''),
        defaultSport,
        sports: sportsData,
        ...(photoData || {
          profileImageProviderUrl: coachData?.profileImageProviderUrl || '',
          profileImageUrl: coachData?.profileImageUrl || '',
          profileImageProviderId: coachData?.profileImageProviderId || '',
          profileImagePath: coachData?.profileImagePath || '',
          profileImageProvider: coachData?.profileImageProvider || MediaProviders.Cloudflare,
          profileImageFileName: coachData?.profileImageFileName || '',
        }),
      };

      // Execute mutation
      const result = await updateOnboardingStep1Mutation({
        variables: mutationVariables,
      });

      if (!result.errors) {
        toast.success('Profile updated successfully!');
        onSubmit(); // Move to next step
      } else {
        toast.error('Failed to update profile. Please try again.');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('An error occurred. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="mt-[4.2rem] flex flex-col gap-2 max-md:mt-[3.75rem]">
        <div className="animate-pulse">
          <div className="mb-4 h-4 w-3/4 rounded bg-gray-200"></div>
          <div className="mb-4 h-4 w-1/2 rounded bg-gray-200"></div>
          <div className="h-4 w-2/3 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(handleSaveAndContinue)}>
      <div className="mt-[4.2rem] flex flex-col gap-2 max-md:mt-[3.75rem]">
        <ProfileItem
          label="Profile photo"
          description="Give the community an easy way to recognize you."
          hasBottomBorder={true}
          invert
          customClass="!mt-0 max-md:mx-0"
          component={
            <Controller
              control={control}
              name="profilePhoto"
              render={({ field: { onChange, value } }) => (
                <Dropzone
                  classNameWrapper="w-full flex flex-col md:flex-row-reverse gap-4"
                  classNameImageContainer="flex aspect-square h-[104px] max-h-[104px] max-w-[104px]"
                  styles="gap-2 rounded-md"
                  multiple={false}
                  isImage={true}
                  invert
                  uploadedFileUrl={typeof value === 'string' ? value : ''}
                  onSelectFiles={async (files) => {
                    onChange(files[0]);
                  }}
                />
              )}
            />
          }
        />
        <ProfileItem
          label="Gender"
          description="Gender is particularly important for parents looking to find a coach for their children."
          hasBottomBorder={true}
          invert
          customClass="max-md:mx-0"
          component={
            <div>
              <RadioList
                listHeader=""
                control={control}
                errors={errors}
                name="gender"
                options={genderOptions}
                isHideOther
                invert
                groupClassName="flex flex-col gap-4"
                labelClassName="max-sm:typography-product-element-label"
              />
              {errors.gender && (
                <p className="mt-2 text-xs text-color-error">{errors.gender.message as string}</p>
              )}
            </div>
          }
        />
        <ProfileItem
          label="Sport"
          description="Sports you are coaching"
          customClass="max-md:mx-0"
          hasBottomBorder={true}
          invert
          component={
            <div>
              <div className="mt- flex flex-col  gap-4">
                {sportsOptions.map((option) => (
                  <CheckboxType
                    key={option.value}
                    name={`sports.${option.value}`}
                    label={option.label}
                    control={control}
                    checkBoxClass="h-4 w-4"
                    textClass="max-sm:typography-product-element-label"
                    labelClassName="flex"
                    invert
                    fillBrand
                  />
                ))}
              </div>
              {errors.sports && (
                <p className="mt-1 text-xs text-color-error">{errors.sports.message as string}</p>
              )}
            </div>
          }
        />
        <ProfileItem
          label="Phone number"
          description="Phone Number"
          invert
          customClass="max-md:mx-0"
          hasBottomBorder={false}
          component={
            <InputField
              fieldLabel=""
              errors={errors}
              placeholder="Enter your phone number"
              name="phoneNumber"
              register={register}
              inputType={InputType.PhoneInput}
              setDialCode={(code: string) => setValue('phoneCountryCode', code)}
              control={control}
              isPhoneGrow
              invert
            />
          }
        />
        <div className="mt-8 flex flex-col items-center justify-center max-sm:mt-2">
          <Button
            type="submit"
            sizeDesktop="lg"
            sizeMobile="md"
            variant="brand"
            size="lg"
            className="w-fit"
            loading={isUpdating || isImageLoading}
            disabled={isUpdating || isImageLoading}
          >
            {isUpdating || isImageLoading ? 'Saving...' : 'Save and continue'}
          </Button>
          <div className="typography-product-caption max-sm:typography-product-chips-filters mt-ds-xl text-center text-color-text-darkmode-tertiary">
            Don't worry — you can edit your information anytime by clicking on your avatar and
            selecting 'My Listing' from the dropdown menu.
          </div>
        </div>
      </div>
    </form>
  );
};

export default ProfileStep;
